import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen, MessageCircle, Target, PenLine, MapPin, ChevronRight, Menu, X, Compass, Users, Sparkles, UserCheck, Quote, Star, ArrowRight } from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-serif text-foreground" data-testid="logo">
              ClarityPath <span className="text-muted-foreground">&mdash;</span> <span className="text-accent">Guided by Mo</span>
            </h1>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium hover:text-primary duration-300">Features</a>
              <a href="#plans" className="text-sm font-medium hover:text-primary duration-300">Plans</a>
              <Link to="/about" className="text-sm font-medium hover:text-primary duration-300" data-testid="about-mo-link">About Mo</Link>
              <a href="#testimonials" className="text-sm font-medium hover:text-primary duration-300">Testimonials</a>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/login')}
                data-testid="login-btn"
                className="rounded-full"
              >
                Log In
              </Button>
              <Button 
                size="sm" 
                onClick={() => navigate('/signup')}
                data-testid="get-started-btn"
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 flex flex-col gap-4">
              <a href="#features" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#plans" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Plans</a>
              <Link to="/about" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>About Mo</Link>
              <a href="#testimonials" className="text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
              <Button variant="outline" onClick={() => navigate('/login')} className="w-full rounded-full">
                Log In
              </Button>
              <Button onClick={() => navigate('/signup')} className="w-full rounded-full bg-primary">
                Get Started
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-36 pb-28 px-6 overflow-hidden min-h-[85vh] flex items-center">
        {/* Foggy/misty layer - fades out */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1603041513984-2a6017b7df38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxmb2dneSUyMG1pc3R5JTIwZm9yZXN0JTIwcGF0aCUyMG1vcm5pbmd8ZW58MHx8fHwxNzc1ODIxNTA0fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(2px)',
            animation: 'heroFogLayer 16s ease-in-out forwards',
          }}
        />
        {/* Sunny/clear layer - fades in crisp */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1770226749182-f14fb625c1c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxvcGVuJTIwY291bnRyeSUyMHBhdGglMjBzdW5zaGluZSUyMGJsdWUlMjBza3klMjBncmVlbiUyMGxhbmRzY2FwZSUyMHdpZGUlMjBob3Jpem9ufGVufDB8fHx8MTc3NTgyMTY5M3ww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'heroSunLayer 16s ease-in-out forwards',
          }}
        />
        {/* Overlay — radial focus behind text, landscape vivid at edges */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 70% 80% at 50% 50%, var(--background) 0%, rgba(255,255,255,0.1) 100%)',
            animation: 'heroOverlay 16s ease-in-out forwards',
          }}
        />
        
        {/* CSS keyframes */}
        <style>{`
          @keyframes heroFogLayer {
            0% { opacity: 1; transform: scale(1); }
            30% { opacity: 0.8; }
            100% { opacity: 0; transform: scale(1.15); }
          }
          @keyframes heroSunLayer {
            0% { opacity: 0; transform: scale(1.06); filter: blur(2px); }
            40% { opacity: 0; }
            70% { opacity: 0.6; filter: blur(0.5px); }
            100% { opacity: 1; transform: scale(1); filter: blur(0px); }
          }
          @keyframes heroOverlay {
            0% { opacity: 0.9; }
            40% { opacity: 0.85; }
            100% { opacity: 0.45; }
          }
          @keyframes heroHeadlineShrink {
            0% { transform: scale(1); opacity: 1; }
            20% { transform: scale(0.98); opacity: 1; }
            100% { transform: scale(0.78); opacity: 0.85; }
          }
          @keyframes heroAccentGrow {
            0% { color: #1a6b6a; transform: scale(1); opacity: 0.8; }
            20% { color: #1a6b6a; transform: scale(1.02); opacity: 0.85; }
            100% { color: #ffffff; transform: scale(1.5); opacity: 1; text-shadow: 0 2px 16px rgba(0,0,0,0.4), 0 0px 4px rgba(0,0,0,0.2); }
          }
          @keyframes heroSubGrow {
            0% { color: var(--foreground); transform: scale(1); opacity: 0.7; }
            20% { color: var(--foreground); transform: scale(1.01); opacity: 0.75; }
            100% { color: #ffffff; transform: scale(1.08); opacity: 1; text-shadow: 0 2px 16px rgba(0,0,0,0.35), 0 0px 4px rgba(0,0,0,0.2); }
          }
        `}</style>
        
        <div className="max-w-2xl mx-auto relative z-10">
          <div className="text-center space-y-6">
            {/* Main headline — stays dark teal, shrinks as scene clears */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-foreground leading-[1.3] tracking-tight" data-testid="hero-heading" style={{ willChange: 'transform, opacity', animation: 'heroHeadlineShrink 16s cubic-bezier(0.4, 0, 0.2, 1) forwards', textShadow: '0 2px 20px rgba(255,255,255,0.5), 0 0px 6px rgba(255,255,255,0.3)' }}>
              Sometimes the hardest part
              <br />
              isn't the journey—
              <br />
              it's stopping to ask
              <br />
              for directions.
            </h1>

            {/* Accent tagline — grows and shifts to white */}
            <p className="text-lg sm:text-xl font-semibold tracking-[0.25em] uppercase" style={{ color: '#1a6b6a', willChange: 'transform, opacity, color', animation: 'heroAccentGrow 16s cubic-bezier(0.4, 0, 0.2, 1) forwards' }}>
              Clarity Changes Everything
            </p>
            
            {/* Subheader — grows slightly and shifts to white */}
            <p className="text-base sm:text-lg text-foreground/90 max-w-lg mx-auto leading-relaxed pt-2" style={{ willChange: 'transform, opacity, color', animation: 'heroSubGrow 16s cubic-bezier(0.4, 0, 0.2, 1) forwards' }}>
              When life feels noisy, I help people move from overwhelm to clarity—so they can trust themselves and take the next right step.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')}
                data-testid="hero-cta-btn"
                className="rounded-full text-base px-10 py-6 bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Find Your Next Step
                <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => {
                  document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="hero-plans-btn"
                className="rounded-full text-base px-8 py-6 bg-white/50 hover:bg-white/70 border-foreground/20 text-foreground/80 hover:text-foreground shadow-sm transition-all duration-300"
              >
                See the Plans
              </Button>
            </div>
          </div>
        </div>
        
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground mb-4">
              Your Clarity Toolkit
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
              Everything you need to pause, reflect, and move forward &mdash; all in one place
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: MessageCircle,
                title: 'Meet CLaiR',
                description: 'Your personal clarity guide available 24/7. Thoughtful questions, grounded insight, no judgment.',
                color: 'text-primary'
              },
              {
                icon: BookOpen,
                title: 'Curated Lessons',
                description: 'Access articles, videos, and exercises designed by life coaching experts.',
                color: 'text-accent'
              },
              {
                icon: Target,
                title: 'Goal Tracking',
                description: 'Set, track, and achieve your personal and professional goals with ease.',
                color: 'text-primary'
              },
              {
                icon: PenLine,
                title: 'Daily Reflections',
                description: 'Journal your thoughts, track your mood, and gain insights into your journey.',
                color: 'text-accent'
              },
              {
                icon: MapPin,
                title: 'Action Plans',
                description: 'Create personalized action plans with step-by-step guidance.',
                color: 'text-primary'
              },
              {
                icon: Users,
                title: 'Small Groups',
                description: 'Join accountability groups with like-minded individuals on similar journeys.',
                color: 'text-accent',
                badge: 'Paid'
              },
              {
                icon: UserCheck,
                title: '1-on-1 Coaching',
                description: 'Get personalized guidance with dedicated sessions from experienced coaches.',
                color: 'text-primary',
                badge: 'Premium'
              },
            ].map((feature, index) => (
              <div
                key={feature.title}
                className="bg-card p-8 rounded-2xl border border-border hover:-translate-y-1 duration-300 shadow-sm hover:shadow-md transition-all relative group"
                data-testid={`feature-card-${index}`}
              >
                {feature.badge && (
                  <div className="absolute top-4 right-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      feature.badge === 'Premium' 
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white'
                        : feature.badge === 'Paid' 
                        ? 'bg-accent/10 text-accent border border-accent'
                        : 'bg-primary/10 text-primary border border-primary'
                    }`}>
                      {feature.badge}
                    </span>
                  </div>
                )}
                <feature.icon className={`${feature.color} mb-4 group-hover:scale-110 transition-transform duration-300`} size={40} />
                <h3 className="text-xl font-serif mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              Getting started is simple — and completely free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Sign up free',
                description: 'Create your account in 30 seconds. No credit card, no commitment — just your name and email.',
              },
              {
                step: '2',
                title: 'Explore at your pace',
                description: 'Journal your thoughts, set your first goal, and meet CLaiR — your AI clarity guide who\'s available 24/7.',
              },
              {
                step: '3',
                title: 'Grow when you\'re ready',
                description: 'Upgrade for deeper tools, curated lessons, community groups, or 1-on-1 coaching with Mo.',
              },
            ].map((item, index) => (
              <div key={item.step} className="text-center space-y-4" data-testid={`how-step-${index}`}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-lg font-serif">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              data-testid="how-it-works-cta"
              className="rounded-full text-base px-8 bg-primary hover:bg-primary/90"
            >
              Get Started — It's Free
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Plans / Pricing Section */}
      <section id="plans" className="py-20 px-6 bg-primary/5 border-y border-primary/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground mb-4">
              Explore the Journey
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              Choose the path that meets you where you are
            </p>
            <p className="text-sm text-muted-foreground/80 mt-3">
              Your journey is 100% confidential. Nothing you share is ever seen by anyone else.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Compass,
                title: 'Open Path',
                price: 'Free',
                subtitle: 'Begin your journey',
                description: 'Start where you are. Reflect, journal, and explore with light guidance as you find your footing.',
                features: ['20 AI messages/month', 'Basic journaling', '1 active goal'],
                bgColor: 'bg-card'
              },
              {
                icon: Target,
                title: 'Supported Path',
                price: '$19/mo',
                subtitle: 'Build momentum',
                description: 'Deepen your growth with consistent support and expanded tools to stay accountable.',
                features: ['Unlimited AI coaching', 'Insight tracking', 'Journal trends'],
                bgColor: 'bg-muted/50'
              },
              {
                icon: Users,
                title: 'Guided Path',
                price: '$49/mo',
                subtitle: 'Full toolkit + community',
                description: 'Access the complete content library and connect with others on similar journeys.',
                features: ['Everything in Supported', 'Content library', 'Small groups'],
                bgColor: 'bg-accent/10',
                popular: true
              },
              {
                icon: Sparkles,
                title: 'Personal Path',
                price: null,
                subtitle: '1:1 guidance with Mo',
                description: 'Receive personalized guidance and direct support for your next chapter.',
                features: ['Everything in Guided', 'Private coaching sessions', 'Priority support'],
                bgColor: 'bg-primary/10',
                isApplication: true
              },
            ].map((item, index) => (
              <div 
                key={item.title} 
                className={`relative text-center p-6 rounded-2xl ${item.bgColor} border ${item.popular ? 'border-accent shadow-md ring-1 ring-accent/20' : 'border-border'} shadow-sm hover:shadow-md transition-all duration-300`} 
                data-testid={`step-${index}`}
              >
                {item.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-white text-xs px-3 py-1 rounded-full font-medium shadow-sm">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 mb-4">
                  <item.icon className="text-accent" size={24} />
                </div>
                <h3 className="text-lg font-serif mb-1">{item.title}</h3>
                {item.isApplication ? (
                  <p className="text-sm text-muted-foreground mb-3 mt-1">{item.subtitle}</p>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-foreground mb-1">{item.price}</p>
                    <p className="text-xs text-muted-foreground mb-3">{item.subtitle}</p>
                  </>
                )}
                <p className="text-muted-foreground text-sm mb-4">{item.description}</p>
                <ul className="text-xs text-left space-y-1.5 mb-4">
                  {item.features.map((feature, i) => (
                    <li key={`feature-${i}`} className="flex items-center gap-2 text-muted-foreground">
                      <span className="text-accent flex-shrink-0">&#10003;</span> {feature}
                    </li>
                  ))}
                </ul>
                {item.isApplication && (
                  <Button 
                    onClick={() => navigate('/apply/coaching')}
                    size="sm"
                    className="rounded-full mt-auto w-full"
                    data-testid="apply-coaching-btn"
                  >
                    Apply for Private Coaching
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="rounded-full"
              data-testid="compare-plans-btn"
            >
              Compare All Plans
              <ChevronRight className="ml-1" size={16} />
            </Button>
          </div>
        </div>
      </section>

      {/* About Mo Teaser */}
      <section className="py-16 px-6 bg-primary/5 border-y border-primary/10">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-serif text-foreground">
            The Person Behind the Path
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "'Georgia', serif" }}>
            "I built ClarityPath because I believe everyone deserves a space to slow down, get honest with themselves, and move forward with confidence. Because I know what it's like to have all the advice in the world — and still feel stuck without the tools to actually move."
          </p>
          <p className="text-accent font-medium mb-2" style={{ fontFamily: "'Dancing Script', cursive", fontSize: '1.5rem' }}>
            &mdash; Mo
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/about')}
            className="rounded-full"
            data-testid="about-mo-teaser-btn"
          >
            Get to Know Mo
            <ChevronRight className="ml-1" size={16} />
          </Button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground mb-4">
              What People Are Saying
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              Real stories from people I've had the honor of working with
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "Mo took her time to listen deeply. I felt she was always fully present with me. I felt safe and comfortable with her as I shared my story. She guided me to embrace my feelings & channel my emotions.",
                name: "Xue X.",
                role: "Nazaré, Portugal",
                stars: 5
              },
              {
                quote: "I love her positive and uplifting energy, her life experience, her open mind and presence. She makes you feel safe to open up and share whatever is troubling you.",
                name: "David C.",
                role: "Boston, MA",
                stars: 5
              },
              {
                quote: "Mo dispelled all my doubts and brought to light my real feelings, fears and thoughts to work on. Gaining clarity on my true feelings made it so much easier to know what I really wanted to do.",
                name: "Iva R.",
                role: "Kent, UK",
                stars: 5
              },
              {
                quote: "I learned so much about myself from the way you guided us through the session. Your insights are instrumental in my relationship and day-to-day life. Thank you!",
                name: "Riley M.",
                role: "Orlando, FL",
                stars: 5
              },
              {
                quote: "I loved your style of group discussions — focused, engaging, and respectful of everyone's journey.",
                name: "Chrissy",
                role: "Columbus, OH",
                stars: 5
              },
              {
                quote: "Every interaction with you has been stellar. You've helped bring clarity to my life. Thank you!",
                name: "Shannon S.",
                role: "Atlanta, GA",
                stars: 5
              },
            ].map((testimonial, index) => (
              <div 
                key={testimonial.name}
                className="bg-card p-8 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-300 relative"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="text-accent/20 absolute top-5 right-5" size={32} />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: testimonial.stars }).map((_, i) => (
                    <Star key={`star-${i}`} className="text-amber-400 fill-amber-400" size={16} />
                  ))}
                </div>
                <p className="text-foreground italic mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-accent font-serif font-bold text-sm">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-foreground text-sm">{testimonial.name}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-serif">
            Your next chapter starts with one step.
          </h2>
          <p className="text-base sm:text-lg opacity-90 max-w-xl mx-auto">
            No commitment required. Start with the free Open Path and explore at your own pace.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => navigate('/signup')}
              data-testid="cta-signup-btn"
              className="rounded-full text-base px-8"
            >
              Start Free Today
              <ChevronRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg" 
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="cta-login-btn"
              className="rounded-full text-base px-8 text-primary-foreground hover:text-primary-foreground/80 hover:bg-white/10"
            >
              Already have an account? Log in
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-muted/30 border-t border-border">
        <div className="max-w-7xl mx-auto text-center text-sm text-muted-foreground">
          <p>&copy; 2026 ClarityPath &mdash; Guided by Mo. Your journey to a better life starts here.</p>
        </div>
      </footer>
    </div>
  );
}
